import lawEvents from "model/party/skills/crime/events/lawEvents";
import Debug from "debug";
const debug = Debug("crime:event:law");

/**
 * The law comes in two places and they cost the same.
 *
 * In a town it is rolled once per robbery, behind the flat 30% gate in
 * EventManager.maybeTriggerPathEvent. On the road it is rolled once per step, against this
 * same 0.3 — so a night spent robbing and a step taken with the law behind you buy the player
 * the same exposure, and there is one number to move if that turns out to be wrong.
 *
 * The road is the half that makes infamy a chase rather than a tax on robbing. A man who robbed
 * a town was safe the instant he walked out of it, which is the one place a posse belongs.
 */
const ROAD_PURSUIT = 0.3;

const lawEventDifficulty = (infamy, crimeLevel) => {
  // Adjust difficulty based on infamy and party level
  const infamyModifier = Math.min(0.5, infamy * 0.1); // Higher infamy increases difficulty
  const levelModifier = Math.max(0, crimeLevel - 1) * 0.1; // Higher level draws a harder encounter
  const difficulty = Math.min(
    1,
    infamyModifier + (1 - infamyModifier) * levelModifier,
  );

  return difficulty;
};

// How much of the law's attention the party has earned, before any per-place gate. Normalised
// on 100 and bent by an exponent below 1, so the early points count for more than the late
// ones: the first robbery anybody remembers moves this further than the fortieth.
const attention = (infamy) => Math.pow(Math.min(1, infamy / 100), 0.65);

/**
 * Which of the three turns up. Shared by the town and the road, so the ladder is the same in
 * both places and only the odds of being looked for at all differ.
 *
 * Returns a thunk rather than the encounter, because that is the shape both callers already
 * hand to getEventVariant. Null when nothing matches: the bands in lawEvents.js are contiguous
 * across 0..1 so this should not happen, but `() => undefined` reaching setEvent would dismiss
 * whatever the player was reading, which is a bad way to find out.
 */
const selectEncounter = (context, onDone) => {
  const { party } = context;
  const infamy = party.traits.getTraitValue("infamy");
  const crimeLevel = party.progression.getCurrentLevelIndex("crime");
  const difficulty = lawEventDifficulty(infamy, crimeLevel);

  const encounter = lawEvents(context, onDone).find(
    (encounter) =>
      encounter.minDifficulty <= difficulty &&
      encounter.maxDifficulty >= difficulty,
  );

  if (!encounter) {
    debug("No law encounter for difficulty", difficulty);
    return null;
  }

  return () => encounter;
};

/* eslint-disable no-unused-vars */
const maybeLawEvent = (context, onDone, level) => {
  const { party } = context;

  // Law enforcement response based on infamy and random chance
  const infamy = party.traits.getTraitValue("infamy");
  const chance = attention(infamy);

  if (Math.random() >= chance) return null;

  debug("High infamy attracts law enforcement!", chance);
  return selectEncounter(context, onDone);
};

// The town's roll, taken on a robbery.
const randomEvent = (context, onDone) => {
  const { party } = context;
  const { progression } = party;

  const level = progression.getCurrentLevel("crime");

  const lawEvent = maybeLawEvent(context, onDone, level);
  return lawEvent;
};

/**
 * The road's roll, taken on a step. Only reached while the law is actually on the party's
 * trail — see party.isPursued() — so the question here is whether they close the distance
 * today, not whether anybody is looking.
 */
const pursuitEvent = (context, onDone) => {
  const { party } = context;
  const infamy = party.traits.getTraitValue("infamy");
  const chance = ROAD_PURSUIT * attention(infamy);

  if (Math.random() >= chance) return null;

  debug("Pursuit closes on the road", chance);
  return selectEncounter(context, onDone);
};

export { pursuitEvent, randomEvent };
